import { FC, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useGetList, useGetMany, useNotify, useTranslate } from 'react-admin';
import {
  BatchAssignField,
  BatchAssignResult,
  BatchAssignUnit,
  useBatchAssignUnits,
} from 'services/api/useBatchAssignUnits';
import {
  BatchAssignToChannelResult,
  BatchAssignToChannelSkipped,
  useBatchAssignUnitsToChannel,
} from 'services/api/useBatchAssignUnitsToChannel';

export interface BatchAssignModalProps {
  open: boolean;
  onClose: () => void;
  units: BatchAssignUnit[];
  onSuccess: (successCount: number, failureCount: number) => void;
}

const FIELD_REFERENCE_MAP: Record<BatchAssignField, string> = {
  designation_id: 'designations',
  locale_id: 'locales',
  update_channel_id: 'update_channels',
  partner_id: 'partners',
};

const ASSIGNABLE_FIELDS: BatchAssignField[] = ['designation_id', 'locale_id', 'update_channel_id', 'partner_id'];

interface ReferenceRecord {
  id: number;
  name: string;
  hardware_revision_id?: number;
  hardware_revision_name?: string;
}

interface SourceChannelOption {
  id: number | null;
  name: string;
}

const BatchAssignModal: FC<BatchAssignModalProps> = ({ open, onClose, units, onSuccess }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { batchAssign } = useBatchAssignUnits();
  const { batchAssign: batchAssignToChannel, loading: channelLoading } = useBatchAssignUnitsToChannel();

  const [selectedField, setSelectedField] = useState<BatchAssignField | ''>('');
  const [selectedValue, setSelectedValue] = useState<ReferenceRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Results for non-channel fields (individual PATCHes)
  const [results, setResults] = useState<BatchAssignResult | null>(null);

  // Results for update_channel_id (bulk API)
  const [channelResults, setChannelResults] = useState<BatchAssignToChannelResult | null>(null);
  const [channelResultsExpanded, setChannelResultsExpanded] = useState({
    updated: false,
    skipped: false,
    notFound: false,
  });

  // Source-channel filter state (null = all channels)
  const [selectedSourceChannels, setSelectedSourceChannels] = useState<SourceChannelOption[] | null>(null);

  // ── Derived from units ─────────────────────────────────────────────────────

  const isChannelField = selectedField === 'update_channel_id';

  const allRevisionIds = useMemo(
    () => [...new Set(units.map((u) => u.hardware_revision_id).filter((id): id is number => id !== undefined))],
    [units],
  );
  const hasMixedHardware = allRevisionIds.length > 1;
  const singleRevisionId = allRevisionIds.length === 1 ? allRevisionIds[0] : undefined;

  // Source-channel options derived from units' current update_channel_id.
  const sourceChannelIds = useMemo(() => [...new Set(units.map((u) => u.update_channel_id ?? null))], [units]);
  const nonNullSourceIds = useMemo(
    () => sourceChannelIds.filter((id): id is number => id !== null),
    [sourceChannelIds],
  );
  const { data: sourceChannelRecords = [] } = useGetMany<ReferenceRecord>(
    'update_channels',
    { ids: nonNullSourceIds },
    { enabled: isChannelField && nonNullSourceIds.length > 0 },
  );
  const namedSourceOptions: SourceChannelOption[] = useMemo(() => {
    const nameMap = new Map(sourceChannelRecords.map((c) => [c.id, c.name]));
    return sourceChannelIds.map((id) =>
      id === null
        ? { id: null, name: translate('resources.units.batch_assign_channel.source_channel_unassigned') }
        : { id, name: nameMap.get(id) ?? String(id) },
    );
  }, [sourceChannelIds, sourceChannelRecords, translate]);
  const showSourceChannelSelector = isChannelField && namedSourceOptions.length > 1;

  const activeSourceChannels =
    selectedSourceChannels !== null && selectedSourceChannels.length > 0 ? selectedSourceChannels : null;

  const serialToChannelId = useMemo(
    () =>
      new Map(
        units
          .filter((u): u is BatchAssignUnit & { serial: string } => Boolean(u.serial))
          .map((u) => [u.serial, u.update_channel_id ?? null]),
      ),
    [units],
  );

  const filteredSerials = useMemo((): string[] => {
    const allSerials = units.map((u) => u.serial).filter((s): s is string => Boolean(s));
    if (!activeSourceChannels) return allSerials;
    const allowedIds = new Set(activeSourceChannels.map((o) => o.id));
    return allSerials.filter((serial) => {
      if (serialToChannelId.has(serial)) return allowedIds.has(serialToChannelId.get(serial) ?? null);
      return true;
    });
  }, [units, activeSourceChannels, serialToChannelId]);

  const { data: referenceOptions, isLoading: isLoadingOptions } = useGetList<ReferenceRecord>(
    selectedField ? FIELD_REFERENCE_MAP[selectedField] : 'designations',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'name', order: 'ASC' },
      filter:
        isChannelField && singleRevisionId !== undefined
          ? { active: true, hardware_revision_id: singleRevisionId }
          : isChannelField
            ? { active: true }
            : {},
    },
    { enabled: !!selectedField },
  );

  const handleFieldChange = (event: SelectChangeEvent<BatchAssignField | ''>) => {
    setSelectedField(event.target.value as BatchAssignField | '');
    setSelectedValue(null);
    setSelectedSourceChannels(null);
  };

  const handleConfirm = async () => {
    if (!selectedField || selectedValue === null) return;

    if (isChannelField) {
      try {
        const result = await batchAssignToChannel(selectedValue.id, filteredSerials);
        setChannelResults(result);
        onSuccess(result.updated.length, result.skipped.length + result.not_found.length);
      } catch (err: unknown) {
        const httpErr = err as { status?: number; body?: { errors?: Record<string, string[]> } };
        if (httpErr?.status === 422 && httpErr?.body?.errors) {
          notify(Object.values(httpErr.body.errors)[0]?.[0] ?? 'resources.units.batch_assign_channel.error_generic', {
            type: 'error',
          });
        } else {
          notify('resources.units.batch_assign_channel.error_generic', { type: 'error' });
          handleClose();
        }
      }
      return;
    }

    setIsLoading(true);
    try {
      const result = await batchAssign(units, selectedField, selectedValue.id);
      setResults(result);
      onSuccess(result.successCount, result.failureCount);
    } catch {
      notify('resources.units.bulk_assign.error_generic', { type: 'error' });
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedField('');
    setSelectedValue(null);
    setSelectedSourceChannels(null);
    setResults(null);
    setChannelResults(null);
    setChannelResultsExpanded({ updated: false, skipped: false, notFound: false });
    setIsLoading(false);
    onClose();
  };

  const unitLabel = (unit: BatchAssignUnit) => unit.serial ?? unit.hostname ?? `Unit ${unit.id}`;

  const isPhaseResults = results !== null || channelResults !== null;
  const isSubmitting = isLoading || channelLoading;
  const isConfirmDisabled =
    !selectedField ||
    selectedValue === null ||
    isSubmitting ||
    units.length === 0 ||
    (isChannelField && hasMixedHardware);

  const translateSkipReason = (item: BatchAssignToChannelSkipped): string => {
    if (item.reason === 'Hardware revision mismatch') {
      if (item.hardware_name || item.hardware_revision_name) {
        return translate('resources.units.batch_assign_channel.skip_reason_hardware_revision_mismatch_details', {
          hardware: item.hardware_name ?? '?',
          hardware_revision: item.hardware_revision_name ?? '?',
        });
      }
      return translate('resources.units.batch_assign_channel.skip_reason_hardware_revision_mismatch');
    }
    if (item.reason === 'No configuration found') {
      return translate('resources.units.batch_assign_channel.skip_reason_no_configuration_found');
    }
    return item.reason;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('resources.units.bulk_assign.dialog_title')}</DialogTitle>

      <DialogContent>
        {/* ── Phase 1: form ── */}
        {!isPhaseResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Field selector */}
            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel id="batch-assign-field-label">
                {translate('resources.units.bulk_assign.field_label')}
              </InputLabel>
              <Select<BatchAssignField | ''>
                labelId="batch-assign-field-label"
                value={selectedField}
                label={translate('resources.units.bulk_assign.field_label')}
                onChange={handleFieldChange}
                displayEmpty
              >
                <MenuItem value="-" disabled>
                  {translate('resources.units.bulk_assign.field_placeholder')}
                </MenuItem>
                {ASSIGNABLE_FIELDS.map((field) => (
                  <MenuItem key={field} value={field}>
                    {translate(`resources.units.bulk_assign.fields.${field}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Mixed hardware warning (channel only — blocks confirm) */}
            {isChannelField && hasMixedHardware && (
              <Alert severity="warning">
                {translate('resources.units.batch_assign_channel.warning_mixed_hardware')}
              </Alert>
            )}

            {/* Source-channel multi-select (channel only, multiple source channels) */}
            {showSourceChannelSelector && !hasMixedHardware && (
              <Alert severity="info" sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {translate('resources.units.batch_assign_channel.info_multiple_source_channels')}
                </Typography>
                <MuiAutocomplete<SourceChannelOption, true>
                  multiple
                  disableCloseOnSelect
                  options={namedSourceOptions}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedSourceChannels ?? namedSourceOptions}
                  onChange={(_, value) => {
                    setSelectedSourceChannels(value.length === namedSourceOptions.length ? null : value);
                  }}
                  disabled={isSubmitting}
                  sx={{ minWidth: 280, mt: 0.5, bgcolor: 'background.paper', borderRadius: 1 }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id ?? 'unassigned'}>
                      <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('resources.units.batch_assign_channel.source_channel_label')}
                      size="small"
                      placeholder={
                        selectedSourceChannels === null
                          ? translate('resources.units.batch_assign_channel.source_channel_all')
                          : undefined
                      }
                    />
                  )}
                />
                {activeSourceChannels && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {translate('resources.units.batch_assign_channel.source_channel_filtered_count', {
                      count: filteredSerials.length,
                      total: units.filter((u) => u.serial).length,
                    })}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Value selector */}
            <Autocomplete<ReferenceRecord>
              options={referenceOptions ?? []}
              getOptionLabel={(option) => option.name}
              groupBy={isChannelField ? (o) => o.hardware_revision_name ?? '' : undefined}
              value={selectedValue}
              onChange={(_event, newValue) => setSelectedValue(newValue)}
              loading={isLoadingOptions}
              disabled={!selectedField || isSubmitting || (isChannelField && hasMixedHardware)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('resources.units.bulk_assign.value_label')}
                  placeholder={translate('resources.units.bulk_assign.value_placeholder')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingOptions && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Mixed hardware warning for non-channel fields */}
            {!isChannelField && hasMixedHardware && (
              <Alert severity="warning">{translate('resources.units.bulk_assign.warning_mixed_hardware')}</Alert>
            )}

            {/* Unit preview list */}
            <List
              dense
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {units.map((unit) => (
                <ListItem key={unit.id}>
                  <ListItemText
                    primary={unitLabel(unit)}
                    secondary={
                      unit.serial && unit.hostname && unit.serial !== unit.hostname ? unit.hostname : undefined
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* ── Phase 2a: results for non-channel fields ── */}
        {results && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {results.succeeded.length > 0 && (
              <>
                <Alert severity="success">
                  {translate('resources.units.bulk_assign.results_success_heading', {
                    count: results.succeeded.length,
                  })}
                </Alert>
                <List dense>
                  {results.succeeded.map((item) => (
                    <ListItem key={item.id}>
                      <ListItemText primary={item.serial ?? item.hostname ?? `Unit ${item.id}`} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {results.failed.length > 0 && (
              <>
                <Alert severity="error">
                  {translate('resources.units.bulk_assign.results_failure_heading', {
                    count: results.failed.length,
                  })}
                </Alert>
                <List dense>
                  {results.failed.map((item) => (
                    <ListItem key={item.id}>
                      <ListItemText
                        primary={item.serial ?? item.hostname ?? `Unit ${item.id}`}
                        secondary={item.error}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}

        {/* ── Phase 2b: results for update_channel_id (bulk API) ── */}
        {channelResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.units.batch_assign_channel.results_summary', {
                updated: channelResults.updated.length,
                skipped: channelResults.skipped.length,
                not_found: channelResults.not_found.length,
                total: channelResults.updated.length + channelResults.skipped.length + channelResults.not_found.length,
              })}
            </Typography>

            {channelResults.updated.length > 0 && (
              <Box>
                <Alert
                  severity="success"
                  action={
                    <IconButton
                      size="small"
                      onClick={() => setChannelResultsExpanded((s) => ({ ...s, updated: !s.updated }))}
                    >
                      {channelResultsExpanded.updated ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  }
                >
                  {translate('resources.units.batch_assign_channel.results_updated_heading', {
                    count: channelResults.updated.length,
                  })}
                </Alert>
                <Collapse in={channelResultsExpanded.updated} timeout={10}>
                  <List dense>
                    {channelResults.updated.map((serial) => (
                      <ListItem key={serial}>
                        <ListItemText primary={serial} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {channelResults.skipped.length > 0 && (
              <Box>
                <Alert
                  severity="warning"
                  action={
                    <IconButton
                      size="small"
                      onClick={() => setChannelResultsExpanded((s) => ({ ...s, skipped: !s.skipped }))}
                    >
                      {channelResultsExpanded.skipped ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  }
                >
                  {translate('resources.units.batch_assign_channel.results_skipped_heading', {
                    count: channelResults.skipped.length,
                  })}
                </Alert>
                <Collapse in={channelResultsExpanded.skipped} timeout={10}>
                  <List dense>
                    {channelResults.skipped.map((item: BatchAssignToChannelSkipped) => (
                      <ListItem key={item.serial}>
                        <ListItemText primary={item.serial} secondary={translateSkipReason(item)} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {channelResults.not_found.length > 0 && (
              <Box>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      size="small"
                      onClick={() => setChannelResultsExpanded((s) => ({ ...s, notFound: !s.notFound }))}
                    >
                      {channelResultsExpanded.notFound ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  }
                >
                  {translate('resources.units.batch_assign_channel.results_not_found_heading', {
                    count: channelResults.not_found.length,
                  })}
                </Alert>
                <Collapse in={channelResultsExpanded.notFound} timeout={10}>
                  <List dense>
                    {channelResults.not_found.map((serial) => (
                      <ListItem key={serial}>
                        <ListItemText primary={serial} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {isPhaseResults
            ? translate('resources.units.bulk_assign.close_button')
            : translate('resources.units.bulk_assign.cancel_button')}
        </Button>

        {!isPhaseResults && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isConfirmDisabled}
            sx={{
              'bgcolor': 'primary.main',
              'color': 'secondary.main',
              'borderRadius': '30px',
              'borderTopRightRadius': '10px',
              'textTransform': 'none',
              '&:hover': {
                bgcolor: 'background.paper',
                borderColor: 'secondary.main',
                border: '1px solid',
                color: 'secondary.main',
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} />
            ) : (
              translate('resources.units.bulk_assign.confirm_button', {
                count: isChannelField ? filteredSerials.length : units.length,
              })
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchAssignModal;
